// ClientDuplicateDialog — Dialog réutilisable pour afficher les doublons
// détectés au moment de la création d'un client ou d'un dossier.
//
// Règle JJ (2026-04-21) : éviter les doublons via UI, lecture seule côté
// backend (endpoints /api/metier/clients/check-duplicate et
// /api/metier/dossiers/check-duplicate-by-client).
//
// Usage :
//   <ClientDuplicateDialog
//     open={hasDuplicates}
//     mode="client"              // "client" | "dossier"
//     matches={duplicates}        // array de matches retournés par l'API
//     onUseExisting={(match) => {}}   // click "Utiliser ce client/dossier"
//     onCreateAnyway={() => {}}       // click "Créer quand même"
//     onCancel={() => {}}             // fermer le dialog
//   />

import { useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { AlertTriangle, User, FolderOpen, CheckCircle2, Clock } from "lucide-react";
import { duplicateDialogLexicon } from "./clientDuplicateLexicon";

function formatDate(d) {
  if (!d) return "";
  let s = d;
  if (typeof s === "string" && !s.endsWith("Z") && !s.slice(-6).match(/[+-]\d\d:\d\d/)) s = `${s}Z`;
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString("fr-FR");
}

function formatAddress(addr) {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  const { rue, code_postal, ville } = addr || {};
  return [rue, code_postal && ville ? `${code_postal} ${ville}` : code_postal || ville]
    .filter(Boolean).join(", ");
}

function ClientMatchCard({ match, selected, onSelect }) {
  return (
    <button
      onClick={() => onSelect(match)}
      className={`w-full text-left p-3 rounded-md border transition-all ${
        selected
          ? "border-[var(--oh-accent)] bg-[var(--oh-accent-bg)]"
          : "border-[var(--oh-border)] bg-[var(--oh-bg-surface)] hover:border-[var(--oh-border)] hover:bg-[var(--oh-bg-elevated)]"
      }`}
      data-testid={`dup-client-card-${match.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
            selected ? "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)]" : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)]"
          }`}>
            <User className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-[var(--oh-text)] truncate">
              {match.civilite && <span className="text-[var(--oh-text-secondary)] font-normal">{match.civilite} </span>}
              {match.prenom} {match.nom}
            </p>
            {match.reference && (
              <p className="text-[11px] font-mono text-[var(--oh-text-muted)] mt-0.5 truncate">{match.reference}</p>
            )}
            {formatAddress(match.adresse) && (
              <p className="text-xs text-[var(--oh-text-secondary)] mt-0.5 truncate">{formatAddress(match.adresse)}</p>
            )}
            {(match.email || match.telephone) && (
              <p className="text-xs text-[var(--oh-text-secondary)] mt-0.5 truncate">
                {match.email}{match.email && match.telephone ? " • " : ""}{match.telephone}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {match.dossiers_actifs_count > 0 && (
            <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border-0 text-xs">
              {match.dossiers_actifs_count} actif{match.dossiers_actifs_count > 1 ? "s" : ""}
            </Badge>
          )}
          {match.dossiers_count > 0 && (
            <span className="text-xs text-[var(--oh-text-secondary)]">
              {match.dossiers_count} dossier{match.dossiers_count > 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>
      {match.last_dossier_ref && (
        <div className="mt-2 pt-2 border-t border-[var(--oh-border)] flex items-center gap-2 text-xs">
          <FolderOpen className="w-3 h-3 text-[var(--oh-text-muted)]" />
          <span className="text-[var(--oh-text-secondary)]">Dernier dossier :</span>
          <span className="font-mono text-[var(--oh-text-secondary)]">{match.last_dossier_ref}</span>
          {match.last_dossier_date && (
            <span className="text-[var(--oh-text-muted)]">• {formatDate(match.last_dossier_date)}</span>
          )}
        </div>
      )}
    </button>
  );
}

function DossierMatchCard({ match, selected, onSelect }) {
  return (
    <button
      onClick={() => onSelect(match)}
      className={`w-full text-left p-3 rounded-md border transition-all ${
        selected
          ? "border-[var(--oh-accent)] bg-[var(--oh-accent-bg)]"
          : "border-[var(--oh-border)] bg-[var(--oh-bg-surface)] hover:border-[var(--oh-border)] hover:bg-[var(--oh-bg-elevated)]"
      }`}
      data-testid={`dup-dossier-card-${match.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
            match.is_active ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-muted)]"
          }`}>
            {match.is_active ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-mono font-medium text-[var(--oh-text)]">{match.reference}</p>
            {match.etablissement && (
              <p className="text-xs text-[var(--oh-text-secondary)] mt-0.5 truncate">{match.etablissement}</p>
            )}
            <p className="text-xs text-[var(--oh-text-secondary)] mt-0.5">
              {match.activite?.toUpperCase()} • {match.statut} • créé le {formatDate(match.created_at)}
            </p>
          </div>
        </div>
        {match.is_active && (
          <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border-0 text-xs flex-shrink-0">Actif</Badge>
        )}
      </div>
    </button>
  );
}

export default function ClientDuplicateDialog({
  open,
  mode = "client",
  matches = [],
  entity,            // surcharge du vocabulaire : "client" | "dossier" (cf. duplicateDialogLexicon)
  onUseExisting,
  onCreateAnyway,
  onCancel,
  // Surcharges optionnelles du texte (ex. avertissement SIREN-soft non bloquant) :
  // si fournies, elles priment sur le lexique. Additif, rétro-compatible.
  title: titleOverride,
  description: descriptionOverride,
  createLabel: createLabelOverride,
  useLabel: useLabelOverride,
}) {
  const [selectedId, setSelectedId] = useState(null);
  // Auto-sélection par défaut du MEILLEUR candidat (matches[0] = score le plus
  // haut, déjà trié desc côté backend), y compris à ≥2 candidats. Sans ça, le
  // cas fuzzy multi-candidats laissait le bouton "Utiliser" disabled tant qu'aucune
  // carte n'était cliquée → "clic sans effet" perçu. L'utilisateur peut toujours
  // changer en cliquant une autre carte (setSelectedId).
  const selected = useMemo(
    () => matches.find(m => m.id === selectedId) || matches[0] || null,
    [matches, selectedId],
  );

  const isDossier = mode === "dossier";   // pilote la FORME des cartes (inchangé)
  const baseLex = duplicateDialogLexicon({ mode, entity });
  const lex = {
    ...baseLex,
    ...(titleOverride != null ? { title: titleOverride } : {}),
    ...(descriptionOverride != null ? { description: descriptionOverride } : {}),
    ...(createLabelOverride != null ? { createLabel: createLabelOverride } : {}),
    ...(useLabelOverride != null ? { useLabel: useLabelOverride } : {}),
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel?.(); }}>
      <DialogContent className="max-w-2xl" data-testid={`duplicate-dialog-${mode}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[var(--oh-info-text)]" />
            {lex.title}
          </DialogTitle>
          <DialogDescription>{lex.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto py-1">
          {matches.map(match => isDossier
            ? <DossierMatchCard key={match.id} match={match} selected={selected?.id === match.id} onSelect={m => setSelectedId(m.id)} />
            : <ClientMatchCard key={match.id} match={match} selected={selected?.id === match.id} onSelect={m => setSelectedId(m.id)} />
          )}
        </div>

        <DialogFooter className="gap-2">
          {lex.showCancel && (
            <Button
              variant="ghost"
              onClick={() => onCancel?.()}
              data-testid="duplicate-dialog-cancel"
            >
              Annuler
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onCreateAnyway?.()}
            data-testid="duplicate-dialog-create-anyway"
          >
            {lex.createLabel}
          </Button>
          <Button
            disabled={!selected}
            onClick={() => selected && onUseExisting?.(selected)}
            data-testid="duplicate-dialog-use-existing"
          >
            {lex.useLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
